import { Chat } from "../../../../core/components/Chat";
import { Column } from "../../../../core/components/Column";
import { Row } from "../../../../core/components/Row";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TextComponent } from "../../../../core/components/TextComponent";
import { ChatMessages } from "../../../../core/components/Messages";
import { SendMessage } from "../../../../core/icons/SendMessage";
import { CustomButton } from "../../../../core/components/CustomButton";
import "./css/EmojiPicker.css";
import { FooterChat } from "./FooterChat";
import { HeaderChat } from "./HeaderChat";
import { useCallback, useEffect, useState, useRef } from "react";
import { AppDispatch, RootState } from "../../../../core/store/store";
import { useDispatch, useSelector } from "react-redux";
import { onAddMessage, onGetMessages, onUpdateMessageRead, sendEmitTempMessages, uploadFile } from "../store/ChatThunk";
import { AddMessageParams } from "../../domain/usecases/addMessage";
import { v4 as uuidv4 } from "uuid";
import { Chats } from "../../domain/entities/getChat";
import { GetMessagesParams } from "../../domain/usecases/getMessages";
import { ChatMessage } from "../../domain/entities/getMessages";
import { deleteHistory, deleteSelectedChat, newMessageReceived, resetUnreadMessages, setSelectedChat } from "../store/ChatSlice";
import { MyNewMatches } from "../components/MyNewMatches";
import { determineCurrentStatus, formatDate } from "../../../../core/constants/constants";
import { deleteNewMatch } from "../../../match/presentation/store/MatchSlice";
import { ChatBloqued } from "../components/ChatBloqued";
import CircularProgress from "../../../../core/components/CircularProgress";
import { ModalOpenImage } from "../components/ModalOpenImage";
import { ArrowDown2 } from "../../../../core/icons/ArrowDown";
import { useAlert } from "../../../../core/hooks/useAlert";
import {
	chatSocketErrorChannel,
	chatSocketLifecycleChannel,
} from "../../data/datasource/ChatSocketDataSource";
import {
	clearMessageDraft,
	getMessageDraftKey,
	loadMessageDraft,
	saveMessageDraft,
} from "./messageDraftStorage";

const LIMITPAGINATION = "20";

export const MessagesScreen = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { chatId } = useParams<{ chatId: string }>();
	const [text, setText] = useState<string | File>("");
	const { user } = useSelector((state: RootState) => state.onboarding);
	const user_id = user?.id || "";
	const { myNewMatches, newMatchesLoading } = useSelector((state: RootState) => state.match);
	const { chats, messageReceived, messagesLoading, messageHistory, selectedChat } = useSelector((state: RootState) => state.chat);
	const dispatch: AppDispatch = useDispatch();
	const [loadingMore, setLoadingMore] = useState(false);
	const [canLoadMore, setCanLoadMore] = useState(true);
	const messageContainerRef = useRef<HTMLDivElement>(null);
	const [isTemp, setIsTemp] = useState<boolean>(false);
	const [isOpenTempMessage, setIsOpenTempMessage] = useState(false);
	const [IsOpenImage, setIsOpenImage] = useState(false);
	const [openUrlImage, setOpenUrlImage] = useState({ messageDirection: "" ,urlImage: "", hourImage: "" });
	const [timeRemaining, setTimeRemaining] = useState(0);
	const timerRef = useRef<NodeJS.Timeout | null>(null);
	const [activeChatId, setActiveChatId] = useState("");
	const [showScrollButton, setShowScrollButton] = useState(false);
	const [timeOutOfView, setTimeOutOfView] = useState<number>(0);
	const { showAlert } = useAlert();
	const activeDraftKeyRef = useRef("");
	const skipNextDraftSaveRef = useRef(false);

	useEffect(() => {
		if (!chatId || selectedChat || chats.length === 0) return;
		const routeChat = chats.find((chat) => chat.user_id === chatId);
		if (routeChat) dispatch(setSelectedChat(routeChat));
	}, [chatId, chats, dispatch, selectedChat]);

	useEffect(() => {
		const nextDraftKey = getMessageDraftKey(
			user_id,
			selectedChat?.user_id || chatId || "",
		);
		if (activeDraftKeyRef.current === nextDraftKey) return;
		activeDraftKeyRef.current = nextDraftKey;
		const savedDraft = loadMessageDraft(nextDraftKey);
		if (text !== savedDraft) {
			skipNextDraftSaveRef.current = true;
			setText(savedDraft);
		}
	}, [chatId, selectedChat?.user_id, text, user_id]);

	useEffect(() => {
		if (skipNextDraftSaveRef.current) {
			skipNextDraftSaveRef.current = false;
			return;
		}
		if (typeof text !== "string") return;
		saveMessageDraft(activeDraftKeyRef.current, text);
	}, [text]);

	useEffect(() => {
		const handleChatError = (event: Event) => {
			const detail = (event as CustomEvent<string>).detail;
			showAlert({
				type: "error",
				message: detail || "Something went wrong. Please try again later.",
			});
		};

		chatSocketErrorChannel.addEventListener("error", handleChatError);
		return () => {
			chatSocketErrorChannel.removeEventListener("error", handleChatError);
		};
	}, [showAlert]);

	const handleUpdateUnreadMessages = useCallback((chat_id: string, userId: string) => {
		dispatch(onUpdateMessageRead({
			params: {
				chat_id: chat_id,
				userId: userId,
			}
		}));
		dispatch(resetUnreadMessages({ chat_id: chat_id }));
	}, [dispatch]);

	const handleResultPagination = useCallback(async (chat_id: string, page: string, limit: string): Promise<ChatMessage[]> => {
		const data: GetMessagesParams = {
			data: {
				chat_id: chat_id,
				page: page,
				limit: limit,
			}
		};
		const result = await dispatch(onGetMessages(data));
		return result.payload as ChatMessage[];
	}, [dispatch]);

	useEffect(() => {
		if (location.pathname === "/messages") dispatch(deleteSelectedChat());
	}, [dispatch]);

	useEffect(() => {
		const lastMessage = messageReceived[messageReceived.length - 1];
		const foundChat = selectedChat && lastMessage &&
			selectedChat.chat_id === lastMessage.chat.chat_id &&
			lastMessage.message.reciever_id === user_id &&
			lastMessage.message.read === false
			? lastMessage
			: null;

		if (selectedChat && foundChat) {
			handleUpdateUnreadMessages(selectedChat.chat_id, selectedChat.user_id);
		}
		messageContainerRef.current?.scrollTo(0, messageContainerRef.current.scrollHeight);
	}, [messageReceived])

	useEffect(() => {
		messageContainerRef.current?.scrollTo(0, messageContainerRef.current.scrollHeight);
	}, [selectedChat?.status_user_id1, selectedChat?.status_user_id2]);

	const fetchMessages = useCallback(async (selectedChat: Chats) => {
		dispatch(deleteHistory());
		setShowScrollButton(false);
		setCanLoadMore(true);
		setActiveChatId(selectedChat.chat_id);
		setLoadingMore(true);
		const resultMessages = await handleResultPagination(selectedChat.chat_id, "1", LIMITPAGINATION);
		messageContainerRef.current?.scrollTo(0, messageContainerRef.current.scrollHeight);
		if (resultMessages.length === 0 || resultMessages.length < Number(LIMITPAGINATION)) {
			setCanLoadMore(false);
		}
		setLoadingMore(false);
		if (resultMessages.length === 0) return;
		const firstSentMessage = resultMessages[0];
		if (firstSentMessage.sender_id === user_id || firstSentMessage.read || (!selectedChat.message_text && !firstSentMessage.is_temp)) return;
		handleUpdateUnreadMessages(selectedChat.chat_id, selectedChat.user_id);
	}, [dispatch, handleResultPagination, handleUpdateUnreadMessages, user_id]);

	useEffect(() => {
		const handleChatReconnect = () => {
			if (!selectedChat || location.pathname === "/messages") return;
			void fetchMessages(selectedChat);
		};

		chatSocketLifecycleChannel.addEventListener("reconnect", handleChatReconnect);
		return () => {
			chatSocketLifecycleChannel.removeEventListener("reconnect", handleChatReconnect);
		};
	}, [fetchMessages, location.pathname, selectedChat]);

	useEffect(() => {
		if (!selectedChat || location.pathname === "/messages") return;
		fetchMessages(selectedChat);
	}, [fetchMessages, location.pathname, selectedChat]);

	const handleScroll = async (e: React.UIEvent<HTMLDivElement>) => {
		if (!selectedChat || !canLoadMore || selectedChat.chat_id !== activeChatId) return;
		const { scrollTop, scrollHeight } = e.currentTarget;
		const oldScrollHeight = scrollHeight;
		if (scrollTop === 0 && !loadingMore) {
			setLoadingMore(true);
			const resultMessages = await handleResultPagination(
				selectedChat.chat_id,(Math.floor(messageHistory.length / Number(LIMITPAGINATION)) + 1).toString(), LIMITPAGINATION);
			if (resultMessages.length === 0 || resultMessages.length < Number(LIMITPAGINATION)) {
				setCanLoadMore(false);
			} else {
				setCanLoadMore(resultMessages.length >= Number(LIMITPAGINATION));
			}
			const newScrollHeight = messageContainerRef.current?.scrollHeight || 0;
			messageContainerRef.current?.scrollTo(0, newScrollHeight - oldScrollHeight);
			setLoadingMore(false);
		}
	};

	const handleSendANewMessage = () => {
		if (myNewMatches.length === 0 && chats.length === 0) {
			navigate("/");
			return;
		}
		if (myNewMatches.length !== 0){
			handleChatClick({
				chat_id: myNewMatches[0].match_id,
				user_id: myNewMatches[0].user_id,
				username: myNewMatches[0].username,
				profile_pictures: [myNewMatches[0].profile_pictures[0].url],
				message_sender_id: "",
				message_id: "",
				message_text: "",
				sent_at: "",
				read: false,
				unread_messages: 0,
				show_last_logged: myNewMatches[0].show_last_logged,
				last_logged_in_time: myNewMatches[0].last_logged_in_time,
				type: "text",
				status: myNewMatches[0].status,
				status_user_id1: myNewMatches[0].status_user_id1,
                status_user_id2: myNewMatches[0].status_user_id2,
			});
		} else {
			handleChatClick(chats[0]);
		}
	}

	const handleChatClick = (chat: Chats) => {
		dispatch(setSelectedChat(chat));
		navigate(`/messages/${chat.user_id}`);
	};

	const handleNewMessage = async (user_id: string, messageID: string, isMatch?: boolean) => {
		if (!selectedChat) return;
		if ((typeof text === "string" && !(text.trim())) || (typeof text !== "string" && !(text instanceof File))) return;
		if (text instanceof File && messageID) {
			dispatch(uploadFile({
				params: {
					chat_id: selectedChat.chat_id,
					message_id: messageID,
					is_temp: isTemp,
					file: text,
				}
			}))
			setIsTemp(false);
		} else {
			const params = new AddMessageParams({
				message_id: messageID,
				chat_id: selectedChat?.chat_id,
				user_id,
				message_text: (text as string).trim(),
				type: "text",
				is_temp: false,
			});
			dispatch(onAddMessage(params)).then(() => {
				messageContainerRef.current?.scrollTo(0, messageContainerRef.current.scrollHeight);
			});
		}
		clearMessageDraft(activeDraftKeyRef.current);
		setText("");
		if (isMatch) dispatch(deleteNewMatch(user_id));
	};

	const handleOpenTemp = (messageDirection: string, id_message: string, urlImage: string, hourImage: string) => {
		setIsOpenTempMessage(true);
		setOpenUrlImage({ messageDirection, urlImage, hourImage });
		dispatch(sendEmitTempMessages(id_message));
		setTimeRemaining(30);
	}

	const navigateBack = () => {
		dispatch(deleteSelectedChat());
		dispatch(deleteHistory());
		setCanLoadMore(true);
		navigate("/messages");
	}

	const handleOpenImage = (messageDirection: string, urlImage: string, hourImage: string) => {
		setOpenUrlImage({ messageDirection, urlImage, hourImage });
		setIsOpenImage(true);
	}
	
	useEffect(() => {
		if (!isOpenTempMessage) return;
		timerRef.current = setInterval(() => {
		  setTimeRemaining((prev) => {
			if (prev <= 1) {
			  clearInterval(timerRef.current as NodeJS.Timeout); 
			  setIsOpenTempMessage(false); 
			  return 0; 
			}
			return prev - 1; 
		  });
		}, 1000);
		return () => {
		  clearInterval(timerRef.current as NodeJS.Timeout); 
		  timerRef.current = null;
		};
	}, [isOpenTempMessage]);

	const handleCloseImage = () => {
		setIsOpenImage(false);
		setOpenUrlImage({ messageDirection: "", urlImage: "", hourImage: "" });
	}
	const handleCloseTempImage = () => {
		setIsOpenTempMessage(false); 
		clearInterval(timerRef.current as NodeJS.Timeout);
		setTimeRemaining(0);
		setOpenUrlImage({ messageDirection: "", urlImage: "", hourImage: "" });
	}

	useEffect(() => {
        const handleScroll = () => {
            if (!messageContainerRef.current) return;
            const { scrollTop, scrollHeight, clientHeight } = messageContainerRef.current;
            const scrollThreshold = 200;
            setShowScrollButton(scrollTop + clientHeight < scrollHeight - scrollThreshold);
        };

        const container = messageContainerRef.current;
        container?.addEventListener('scroll', handleScroll);
        return () => {
            container?.removeEventListener('scroll', handleScroll);
        };
    }, [selectedChat?.chat_id]);

	const handleScrollToBottom = () => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTo({ top: messageContainerRef.current.scrollHeight, behavior: 'smooth' });
        }
    };

	const handleSendMessage = (create_message_id: string) => {
		const matchData = myNewMatches?.find(m => m.user_id === chatId);
		const chatData = chats.find(m => m.user_id === chatId);
		if (matchData) {
			handleNewMessage(matchData.user_id, create_message_id, true);
		} else if (chatData) {
			handleNewMessage(chatData.user_id, create_message_id, false);
		}
	}

	useEffect(() => {
		const handleVisibilityChange = async () => {
		  if (document.visibilityState === 'visible') {
			if (timeOutOfView >= 10) {
			  if (!selectedChat || location.pathname === "/messages") return;
			  fetchMessages(selectedChat);
			}
			setTimeOutOfView(0);
		  } else {
			const timer = setInterval(() => {
			  if (timeOutOfView < 10) setTimeOutOfView((prevTime) => prevTime + 1);
			  if(document.visibilityState === 'visible') {
				clearInterval(timer);
				setTimeOutOfView(0);
			  }
			}, 1000);
			return () => clearInterval(timer);
		  }
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => {
		  document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	  }, [timeOutOfView]);

	return (
		<Row className="bg-white border border-grayLight max-md:border-b-0 h-full min-h-0 flex-grow">
			<ModalOpenImage 
				isOpen={IsOpenImage} 
				openUrlImage={openUrlImage} 
				infoImage={openUrlImage.messageDirection === "receive" ? {
					username: selectedChat?.username,
					picture: selectedChat?.profile_pictures?.[0],
				}: {
					username: "You",
					picture: user?.profile_pictures[0].url
				}}
				onClose={handleCloseImage}
			/>
			<ModalOpenImage 
				isOpen={isOpenTempMessage} 
				openUrlImage={openUrlImage} 
				infoImage={openUrlImage.messageDirection === "receive" ? {
					username: selectedChat?.username,
					picture: selectedChat?.profile_pictures?.[0]
				}: {
					username: "You",
					picture: user?.profile_pictures[0].url
				}}
				onClose={handleCloseTempImage}
			>
				<TextComponent 
					variant="h2" 
					className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-socondary font-bold text-center w-full"
				>
					Will close in {timeRemaining} seconds.
				</TextComponent>
			</ModalOpenImage>
			<Column className={`${chatId && "max-md:hidden"} w-full min-h-0 md:min-w-fit md:max-w-fit lg:min-w-[25rem] lg:max-w-[25rem]`}>
				<MyNewMatches handleChatClick={handleChatClick} />
				<Row className="sticky top-0 bg-white p-6 border-r border-b border-r-grayLight border-b-grayLight">
					<TextComponent variant="nh4" className="md:hidden lg:flex font-semibold">
						Messages
					</TextComponent>
				</Row>
				<Column className="border-r border-grayLight h-full min-h-0 flex-grow overflow-y-auto">
					{chats.map((chat) => (
						<div
							key={chat.chat_id}
							onClick={() => chat && handleChatClick(chat)}
						>
							<Chat
								{...(chat as Chats)}
							/>
						</div>
					))}
				</Column>
			</Column>
			<Column className={`relative ${!chatId && "max-md:hidden"} md:flex w-full min-h-0 max-md:bg-white max-md:z-10`}>
				<img
					className="absolute top-0 left-0 object-cover w-full h-full select-none"
					src="/bg-chat.svg"
					draggable={false}
					alt="bg-messages"
				/>
				{selectedChat && chatId ? (
					<>
						<HeaderChat
							key={`header-${selectedChat.user_id}`}
							navigateBack={navigateBack}
							selectedChat={selectedChat}
							currentStatus={determineCurrentStatus(selectedChat)}
						/>
						<button 
							key={`down-${selectedChat.user_id}`}
							className={`flex justify-center items-center absolute w-10 bg-white text-primary shadow-md rounded-full aspect-square bottom-28 right-4 lg:right-10 z-[5] transition-opacity duration-300 ${
								showScrollButton ? "opacity-100" : "opacity-0 pointer-events-none invisible"
							}`}
							onClick={handleScrollToBottom}
						>
							<ArrowDown2 />
						</button>
						<div
							ref={messageContainerRef}
							className="z-[1] relative flex-1 min-h-0 overflow-y-auto"
							onScroll={handleScroll}
							key={`history-${selectedChat.user_id}`}
						>
							<Column
								className="p-4 md:p-6 gap-3"
								mainAxisAlignment="end"
							>
								{loadingMore && <CircularProgress size={48}/>}
								{messageHistory && (() => {
									let currentDay = "";
									return messageHistory
										.filter(msg => msg.chat_id === selectedChat.chat_id)
										.reverse()
										.map((msg) => {
											const messageDay = formatDate(msg.sent_at, true);
											const isNewDay = messageDay !== currentDay;
											const messageDirection = msg.reciever_id === user_id ? "receive" : "send";
											if (isNewDay) currentDay = messageDay;
											
											return (
												<>
													{isNewDay &&
														<TextComponent
															key={`date-${msg.message_id}`}
															variant="nh6"
															className="text-center bg-gray-50 rounded-md shadow-lg p-2 text-graySecondary w-fit self-center select-none"
														>
															{messageDay}
														</TextComponent>
													}
													<ChatMessages
														key={`message-${msg.message_id}`}
														variant={messageDirection}
														hour={msg.sent_at}
														message={msg.message_text}
														read={msg.read}
														type={msg.type}
														is_temp={msg.is_temp}
														loading={msg.loading}
														{...((msg.type === "image" || msg.type === "file") && { handleOpenImage: () => handleOpenImage(messageDirection, msg.message_text, msg.sent_at) })}
														{...(msg.is_temp && msg.reciever_id === user_id && { isOpenTemp: () => handleOpenTemp(messageDirection, msg.message_id, msg.message_text, msg.sent_at) })}
													/>
												</>
											);
										});
								})()}
								{!((selectedChat.status_user_id1 === "active" && selectedChat.status_user_id2 === "active") || (selectedChat.status_user_id1 === "matched" && selectedChat.status_user_id2 === "matched")) ? (
									<>
										{selectedChat.status_user_id2 !== "active" ? 
											<ChatBloqued 
												id={selectedChat.chat_id}
												name={selectedChat.username} 
												variant="text"
												user_id={selectedChat.user_id}
												status={selectedChat.status_user_id2}
												status_user={selectedChat.status_user_id1}
												isHistory={messageHistory.length > 0}
											/>
											:
											<ChatBloqued 
												id={selectedChat.chat_id}
												name={selectedChat.username} 
												user_id={selectedChat.user_id}
												status={selectedChat.status_user_id1} 
												status_user={selectedChat.status_user_id2}
												isHistory={messageHistory.length > 0}
											/>
										}
									</>
								):(
									<>
										{(selectedChat.status === "banned" || selectedChat.status === "inactive") && (
											<ChatBloqued 
												id={selectedChat.chat_id}
												name={selectedChat.username} 
												status="inactive"
												user_id={selectedChat.user_id}
												status_user={selectedChat.status_user_id1}
												isHistory={messageHistory.length > 0}
											/>
										)}
									</>
								)}
							</Column>
						</div>
						{(selectedChat.status === "active" || selectedChat.status === "suspended") && ((selectedChat.status_user_id1 === "active" && selectedChat.status_user_id2 === "active") || (selectedChat.status_user_id1 === "matched" && selectedChat.status_user_id2 === "matched")) && 
							<FooterChat
								key={`footer-${selectedChat.user_id}`}
								onSendMessage={() => {
									const create_message_id = uuidv4();
									const now = new Date().toISOString();
									if ((typeof text === "string" && !(text.trim()))) return;
									dispatch(newMessageReceived({
										chat: {
											...selectedChat,
											loading: true,
											unread_messages: 0,
										},
										message: {
											message_id: create_message_id,
											chat_id: selectedChat.chat_id,
											sender_id: user_id,
											reciever_id: selectedChat.user_id,
											message_sender_id: user_id,
											sent_at: now,
											is_temp: false,
											message_text: (text as string).trim(),
											read: false,
											type: "text",
											loading: true,
										}
									}));
									handleSendMessage(create_message_id);
								}}
								handleMessagePhoto={() => {
									const create_message_id = uuidv4();
									const now = new Date().toISOString();
									if ((typeof text !== "string" && !(text instanceof File))) return;
									dispatch(newMessageReceived({
										chat: {
											...selectedChat,
											loading: true,
											unread_messages: 0,
										},
										message: {
											message_id: create_message_id,
											chat_id: selectedChat.chat_id,
											sender_id: user_id,
											reciever_id: selectedChat.user_id,
											message_sender_id: user_id,
											sent_at: now,
											is_temp: isTemp,
											message_text: URL.createObjectURL(text as File),
											read: false,
											type: "file",
											loading: true,
										}
									}));
									handleSendMessage(create_message_id);
								}}
								setText={setText}
								text={text}
								setIsTemp={setIsTemp}
								isTemp={isTemp}
							/>
						}
					</>
				) : (
					<>
						{!newMatchesLoading && !messagesLoading && 
							<Column
								crossAxisAlignment="center"
								mainAxisAlignment="center"
								className="z-[2] max-md:hidden md:h-full text-primary w-fit gap-10 self-center"
							>
								<SendMessage size={142} />
								<CustomButton className="w-fit" color="white" onClick={handleSendANewMessage}>
									<TextComponent variant="h4">
										{myNewMatches.length === 0 && chats.length === 0 ? "Find people" : "Send a new message"}
									</TextComponent>
								</CustomButton>
							</Column>
						}
					</>
				)}
			</Column>
		</Row>
	);
};
